/**
 * "Retry Estimate": asks the server to re-run an Edward Jones quick estimate from its saved answers
 * (`POST /client/:clientId/recalculate-quick-estimate`), without changing any of them.
 *
 * Offered where a quick estimate has no result because its model run failed: the clients list menu
 * of an "Algorithms error" client, and "Edit Quick Estimate" saved with no changes. On success the
 * client and the clients list are refetched, which clears the error status there. On failure the
 * server's reason is shown: a models outage says to try again, answers the EDJ input cannot use say
 * to edit them.
 */
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { quickEstimateApi } from '@client/api/waterlily';
import { fetchClientByClientIdRequest } from '@client/model/clientModel';
import { fetchClientsForAdvisorByAdvisorIdRequest } from '@client/model/clientsModel';
import { selectSessionAdvisor } from '@client/model/selectors';

export function useRecalculateQuickEstimate({ onSuccess }: { onSuccess?: () => void } = {}) {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);

  const mutation = useMutation({
    mutationFn: (clientId: string) => quickEstimateApi.recalculate(clientId),
    onMutate: () => {
      toast.info('Recalculating the quick estimate…');
    },
    onSuccess: (_data, clientId) => {
      dispatch(fetchClientByClientIdRequest(clientId));
      if (advisor?.advisorId) {
        dispatch(fetchClientsForAdvisorByAdvisorIdRequest({ advisorId: advisor.advisorId, isInvalidation: true }));
      }
      toast.success('Quick estimate calculated');
      onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : 'The estimate could not be calculated. Please try again.',
      );
    },
  });

  return {
    recalculateQuickEstimate: mutation.mutate,
    isRecalculating: mutation.isPending,
  };
}
